import { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useFormGuard } from '../hooks/useFormGuard';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { CelebratePhase, AskPhase } from './followup/FollowUpPhases';
import { FollowUpForm } from './followup/FollowUpForm';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

function buildInitialForm(sourceTitle, typeLabel, sourceBizId) {
  return {
    title: `Follow up: ${sourceTitle}`,
    description: `Follow-up from completed ${typeLabel.toLowerCase()}: "${sourceTitle}"`,
    priority: 'MEDIUM',
    estimatedMinutes: 30,
    businessId: sourceBizId,
    dueDate: '',
    energy: 'MEDIUM',
    status: 'TODAY',
    schedulePreference: 'today',
    scheduledDateTime: '',
    recurrenceRule: 'NONE',
    recurrenceEndDate: '',
    delegatedTo: '',
    delegationNotes: '',
    taskType: 'STANDARD',
    labels: [],
    subtasks: [],
  };
}

export default function FollowUpModal({ completedItem, onClose, onCreateFollowUp }) {
  useBodyScrollLock();
  const { businesses } = useBusiness();
  const [phase, setPhase] = useState('celebrate'); // celebrate → ask → form
  const [showForm, setShowForm] = useState(false);
  const celebrateTimerRef = useRef(null);
  const todayDate = formatDate(new Date());
  const tomorrowDate = formatDate(new Date(Date.now() + 86400000));

  const sourceTitle = completedItem?.title || completedItem?.contactName || completedItem?.name || '';
  const sourceType = completedItem?.sourceType || 'task';
  const sourceBizId = completedItem?.businessId || businesses[0]?.id || '';
  const typeLabel = { task: 'Task', call: 'Call', payment: 'Payment', reading: 'Reading' }[sourceType] || 'Item';

  const [form, setForm] = useState(() => buildInitialForm(sourceTitle, typeLabel, sourceBizId));

  const initialTitle = `Follow up: ${sourceTitle}`;
  const initialDesc = `Follow-up from completed ${typeLabel.toLowerCase()}: "${sourceTitle}"`;
  const isFormDirty = showForm && (
    form.title !== initialTitle || form.description !== initialDesc ||
    form.priority !== 'MEDIUM' || form.dueDate !== '' || form.delegatedTo !== ''
  );

  const getFormData = useCallback(() => form, [form]);
  const setFormDataFromDraft = useCallback((data) => setForm(data), []);
  const { guard, showDialog, discard, cancel, save, saveDraft, hasDraft, restoreDraft, clearDraft } = useFormGuard(isFormDirty, {
    draftKey: 'followup',
    getFormData,
    setFormData: setFormDataFromDraft,
  });
  const guardedCloseRef = useRef();
  guardedCloseRef.current = () => guard(onClose);
  const guardedClose = useCallback(() => guardedCloseRef.current?.(), []);

  useEffect(() => {
    celebrateTimerRef.current = setTimeout(() => setPhase('ask'), 1800);
    return () => clearTimeout(celebrateTimerRef.current);
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') guardedClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [guardedClose]);

  const handleSubmitFollowUp = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    onCreateFollowUp(form);
    onClose();
  };

  if (phase === 'celebrate') {
    return <CelebratePhase typeLabel={typeLabel} onClose={onClose} />;
  }

  if (phase === 'ask' && !showForm) {
    return (
      <AskPhase
        sourceTitle={sourceTitle}
        onNo={() => onClose()}
        onYes={() => { clearTimeout(celebrateTimerRef.current); setPhase('form'); setShowForm(true); }}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(8px)' }}>
      <div className="flex min-h-full items-center justify-center px-4 py-8" onClick={(e) => { if (e.target === e.currentTarget) guardedClose(); }}>
      <div className="w-full max-w-[560px] rounded-[30px] animate-fade-in overflow-hidden" style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: 'var(--pd-border)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: 'var(--pd-success)' }}>Follow-up task</div>
            <h3 className="text-lg font-medium mt-2" style={{ letterSpacing: '-0.04em', color: 'var(--pd-text)' }}>
              Create a follow-up for this {typeLabel.toLowerCase()}
            </h3>
          </div>
          <button data-testid="followup-form-close" onClick={guardedClose} className="pd-icon-button"><X className="w-5 h-5" /></button>
        </div>

        <FollowUpForm
          form={form}
          setForm={setForm}
          businesses={businesses}
          typeLabel={typeLabel}
          hasDraft={hasDraft}
          restoreDraft={restoreDraft}
          clearDraft={clearDraft}
          todayDate={todayDate}
          tomorrowDate={tomorrowDate}
          onSubmit={handleSubmitFollowUp}
          guardedClose={guardedClose}
        />
      </div>
      </div>
      <UnsavedChangesDialog
        open={showDialog}
        onDiscard={() => { clearDraft(); discard(); }}
        onCancel={cancel}
        onSave={() => save(() => { onCreateFollowUp(form); onClose(); })}
        onSaveDraft={saveDraft}
      />
    </div>
  );
}
